import { useEffect, useState } from "react";

/** Ticks once a second while `startedAt` is set, returning elapsed seconds since then. */
export function useElapsedSeconds(startedAt?: string | null): number {
  const [elapsed, setElapsed] = useState(() => (startedAt ? Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000) : 0));

  useEffect(() => {
    if (!startedAt) {
      setElapsed(0);
      return;
    }
    const tick = () => setElapsed(Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [startedAt]);

  return elapsed;
}

export function formatDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

export function formatDurationClock(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  return [hours, minutes, seconds].map((n) => String(n).padStart(2, "0")).join(":");
}

export function formatHoursLabel(totalSeconds: number): string {
  const hours = totalSeconds / 3600;
  if (hours < 1) return `${Math.round(totalSeconds / 60)} min`;
  return `${hours.toFixed(1)} hrs`;
}

/**
 * Renders a start/end delay (seconds, +late / -early) as a short human label,
 * e.g. "2h 15m late", "45m early", "right on time".
 */
export function formatDelayLabel(delaySeconds: number, onTimeWindowSeconds = 60): string {
  if (Math.abs(delaySeconds) <= onTimeWindowSeconds) return "right on time";
  return `${formatDuration(Math.abs(delaySeconds))} ${delaySeconds > 0 ? "late" : "early"}`;
}
